import { TRADE_COLORS } from './integralDeltaTrades';

/**
 * THE ACCELERATING FAN, drawn as it steepened.
 *
 * <h2>What a fan is, and why one line is not it</h2>
 *
 * The fan is anchored at a trade's ENTRY and re-sloped to each new swing pivot, keeping whichever
 * is steepest. Its break ends the premise. A chart drawing only the final line draws a trendline
 * — which is a different and weaker claim, because the whole thesis of the rule is that the line
 * got steeper as the advance accelerated.
 *
 * <p>So each line the fan held is drawn as its own segment, from the entry to the point the NEXT
 * pivot took over. The picture is a fan of successive segments rather than a sheaf of lines all
 * reaching the exit.
 *
 * <h2>THE BOUND: a line appears only once the playhead reaches the pivot that made it</h2>
 *
 * A fan line is established by a swing pivot, and a pivot is a price extreme of a bar. Drawing a
 * line whose pivot is ahead of the playhead would put on screen a swing the reader has not been
 * shown — the look-ahead the whole replay exists to prevent, and the one the request named.
 *
 * <p>It is bounded by DATE rather than by index, for the reason the ledger markers are: the
 * transport steps BACKWARDS, and an operator who walks back past a pivot must stop seeing the
 * line it established. A one-way reveal is worse than none.
 *
 * <p>The segment is additionally clipped at the playhead, so a line established behind it is not
 * drawn forward into bars that have not been revealed. The pivot gate alone would leave the tail
 * of each segment reaching into the future.
 */

export const FAN_SERIES_ID = 'dynaman-fan-lines';
export const FAN_OVERLAY_ID = 'dynaman-fan-lines';

/** Dashed and thin: this is a RULE's working, drawn behind the price rather than over it. */
export const FAN_WIDTH = 1.2;
export const FAN_OPACITY = 0.75;
export const FAN_TYPE = 'dashed';

/**
 * The ACTIVE line is solid and the superseded ones stay dashed.
 *
 * <p>Without that a reader cannot tell which of three lines the trade is actually being tested
 * against — which is the one thing the fan is for. The superseded ones are kept rather than
 * removed because the sequence IS the claim; they just stop being the answer.
 */
export const FAN_ACTIVE_TYPE = 'solid';

/** Long is the engine's own buy colour, short its sell colour: the fan belongs to its trade. */
export const fanColour = (direction) =>
  (direction === 'SHORT' ? TRADE_COLORS.algoSell : TRADE_COLORS.algoBuy);

/**
 * Every fan segment to draw at this playhead, as `markLine` coordinate pairs.
 *
 * @param trades   the timeline's completed trades, each optionally carrying `fanLines`
 * @param upToDate the playhead's session date. Null draws nothing, which is the honest reading
 *                 of "no playhead": this is a replay overlay and a bound it cannot evaluate is
 *                 not a bound it should ignore
 */
export function fanMarks(trades, upToDate) {
  const list = Array.isArray(trades) ? trades : [];
  if (!upToDate) return [];
  const out = [];

  list.forEach((entry) => {
    // The timeline's trades arrive stamped (`{ frameIndex, beforeWindow, trade }`), and the
    // research path hands them bare. Both are accepted rather than one being normalised at the
    // call site, where a second shape is a second thing to remember.
    const trade = entry && entry.trade ? entry.trade : entry;
    if (!trade || !Array.isArray(trade.fanLines) || trade.fanLines.length === 0) return;

    const colour = fanColour(trade.direction);
    const visible = trade.fanLines.filter((l) => l && day(l.pivotTime) <= upToDate);

    visible.forEach((line, i) => {
      const from = day(line.fromTime);
      const to = day(line.toTime);
      if (!from || !to) return;
      // CLIPPED AT THE PLAYHEAD. A segment runs from the entry to where the next pivot took
      // over, and that end can be ahead of where the reader has got to.
      const clipped = to > upToDate;
      const endTime = clipped ? upToDate : to;
      const endPrice = clipped
        ? priceAt(line, from, upToDate) : line.toPrice;
      if (endPrice === null || endTime < from) return;

      const active = i === visible.length - 1;
      out.push([
        {
          coord: [from, line.fromPrice],
          lineStyle: {
            color: colour,
            width: FAN_WIDTH,
            opacity: FAN_OPACITY,
            type: active ? FAN_ACTIVE_TYPE : FAN_TYPE,
          },
          label: { show: false },
        },
        { coord: [endTime, endPrice] },
      ]);
    });
  });

  return out;
}

/** The session date of an engine timestamp, string-sliced — never through `Date`. */
function day(iso) {
  if (typeof iso !== 'string' || iso.length < 10) return null;
  const d = iso.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

/**
 * The line's price at the playhead, interpolated between its two shipped endpoints.
 *
 * <h2>Interpolated on CALENDAR days, and the approximation is deliberate</h2>
 *
 * The backend ships two (date, price) pairs precisely so the browser does no geometry against the
 * engine's bar index — which is the signal timeframe's and not the axis's. Clipping needs a point
 * strictly between them, and the only ordinate both sides agree on is the date.
 *
 * <p>Weekends make that slightly uneven: a segment spanning one costs the clip a fraction of a
 * bar. The alternative is the index arithmetic the endpoints exist to avoid, and the error is a
 * few pixels on a segment whose purpose is to show a SLOPE steepening.
 */
function priceAt(line, fromDate, atDate) {
  const a = Date.parse(`${fromDate}T00:00:00Z`);
  const b = Date.parse(`${day(line.toTime)}T00:00:00Z`);
  const at = Date.parse(`${atDate}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(at) || b <= a) return null;
  const t = Math.max(0, Math.min(1, (at - a) / (b - a)));
  return line.fromPrice + t * (line.toPrice - line.fromPrice);
}

/** The series payload, declared even when empty — a merge cannot remove a mark by omitting it. */
export function fanLineMarks(trades, upToDate) {
  return {
    markLine: {
      silent: true,
      symbol: 'none',
      animation: false,
      data: fanMarks(trades, upToDate),
    },
  };
}

export default fanLineMarks;
